import React, { useEffect, useRef } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

import { button, input, authForm, contactForm } from "./auth-forms.module.scss";
import { config } from "../../../config";
import useUser from "../hooks/use-user";

import Button from "../../../components/atoms/button";
import { useI18next } from "../../../../plugins/gatsby-plugin-ap-i18next/src/useI18next";
import FormikForm from "../../../components/hoc/formik-form";
import Input from "../../../components/atoms/form/input";
import Checkbox from "../../../components/atoms/form/checkbox";
import { IUserRegister } from "../models/payloads.model";
import { useModalContext } from "../../../contexts/modal.context";
import { navigate } from "gatsby";

export interface IRegisterProps {
    className?: string;
    context?: Record<string, string | number>;
    modalId?: number;
    showLogin?: boolean;
}

const { required, requiredConsent, email } = config.messages.form;

// @ts-ignore
const initialValues: IUserRegister = {
    profiles: [{ firstName: "" }],
    context: {},
    email: "",
    password: "",
    consents: false,
};

const signupSchema = Yup.object({
    profiles: Yup.array(Yup.object({ firstName: Yup.string().required(required) })),
    email: Yup.string().email(email).required(required),
    password: Yup.string().required(required),
    consents: Yup.boolean().required(requiredConsent).oneOf([true], requiredConsent),
});

const RegisterForm: React.FC<IRegisterProps> = ({ className = "", context, modalId, showLogin = true }) => {
    const { t } = useI18next();
    const firstInputRef = useRef<HTMLInputElement>(null);
    const { removeModal } = useModalContext();

    const { register, logIn } = useUser();

    useEffect(() => {
        if (!firstInputRef.current) return;
        firstInputRef.current.focus();
    }, []);

    const login = () => {
        if (modalId) {
            removeModal(modalId);
        }
        navigate('/auth')
    }
    const handleRegister = async (values: IUserRegister) => {
        try {
            console.log("Register values:", values); // Logowanie danych
            await register(values);
            const maxRetries = 5; // Maksymalna liczba prób (3 próby: 1000ms, 2000ms, 5000ms)
            let attempt = 0;
            let delay = 1000; // Początkowy timeout

            while (attempt < maxRetries) {
                try {
                    console.log(`Login attempt ${attempt + 1} with delay ${delay}ms`);
                    await new Promise((resolve) => setTimeout(resolve, delay)); // Oczekiwanie
                    await logIn({ email: values.email, password: values.password });
                    setTimeout(() => {
                        navigate("/generator/wyczaruj-stylizacje");
                    }, 1000); // Dodatkowe opóźnienie przed nawigacją
                    return; // Zakończ, jeśli logowanie się powiedzie
                } catch (error) {
                    console.error(`Login attempt ${attempt + 1} failed:`, error);
                    attempt++;
                    delay *= 2; // Podwój czas oczekiwania (1000ms -> 2000ms -> 5000ms)
                }
            }
        } catch (error) {
            console.error("Registration failed:", error); // Obsługa błędów
        }
    };
    return (
        <Formik
            initialValues={{ ...initialValues, context }}
            validationSchema={signupSchema}
            validateOnBlur={false}
            onSubmit={handleRegister}
        >
            {(formik) => (
                <FormikForm
                    formik={formik}
                    successMessage={""}
                    className={`${authForm} ${className}`}
                >
                    <Input
                        ref={firstInputRef}
                        name="profiles[0]firstName"
                        type="text"
                        placeholder={t("form.label.name")}
                        className={input}
                    />

                    <Input
                        name="email"
                        type="email"
                        placeholder={t("form.label.email")}
                        className={input}
                        autoComplete="username"
                    />

                    <Input
                        name="password"
                        type="password"
                        placeholder={t("form.label.password")}
                        className={input}
                        autoComplete="new-password"
                    />

                    <Checkbox name="subscribeNewsletter" className={contactForm}>
                        <ReactMarkdown
                            //@ts-ignore
                            rehypePlugins={[rehypeRaw]}
                        >
                            {t("form.register.newsletter")}
                        </ReactMarkdown>
                    </Checkbox>

                    <Checkbox className={contactForm} name="consents">
                        <ReactMarkdown
                            // @ts-ignore
                            rehypePlugins={[rehypeRaw]}
                        >
                            {t("form.consent.privacy")}
                        </ReactMarkdown>
                    </Checkbox>
                    {showLogin && <p style={{ 'cursor': 'pointer', 'padding': '5px', 'textAlign': 'center', 'fontSize': '1.4rem' }} onClick={login}>
                        Masz już konto? <u>Kliknij tutaj aby się zalogować</u>
                    </p>}
                    <Button color="primary" type="filled" htmlType="submit" className={button}>
                        {t("button.createAccount")}
                    </Button>
                </FormikForm>
            )}
        </Formik>
    );
};

export default RegisterForm;
